import { cloneDeep } from "lodash";
import { DragEvent, FC, JSX, useRef } from "react";

interface DragAndDropReorderingProps {
  /**
   * Determines whether a drag and drop item should be draggable
   */
  draggable: boolean;
  /**
   * The list of items to be re-ordered;
   * Each item must have a unique-id at the highest element level
   */ 
  items: JSX.Element[];
  /**
   * The function that will return the new array of re-ordered items
   */
  onDragAndDropChange: (items: JSX.Element[]) => void;
  /**
   * An option prop that that indicates to a higher-level component that dragging has stopped
   */
  onDragEnd?: () => void;
}

let draggedItem: HTMLSpanElement = null;

const DragAndDropReordering: FC<React.PropsWithChildren<DragAndDropReorderingProps>> = (props: DragAndDropReorderingProps) => {
  const containerRef: React.RefObject<HTMLSpanElement> = useRef<HTMLSpanElement>(null);

  const handleDragStart = (e: DragEvent<HTMLSpanElement>): void => {
    // capture the item being dragged
    draggedItem = e.target as HTMLSpanElement;

    // apply a "dropspot" outline to all other elements
    [...containerRef.current.querySelectorAll(".drag-and-drop__item")].filter(
      (item) => item.getAttribute("data-group-id") !== (e.target as HTMLSpanElement).closest(".drag-and-drop__item").getAttribute("data-group-id")
    ).forEach((item) => item.classList.add("dropspot"));
  };

  const handleDragEnd = (): void => {
    // remove the "dropspot" outline on all other elements
    [...containerRef.current.querySelectorAll(".drag-and-drop__item")].forEach((item) => item.classList.remove("dropspot"));

    if (!!props.onDragEnd) {
      props.onDragEnd();
    }
  };

  const handleDragOver = (e: DragEvent<HTMLSpanElement>): void => {
    e.preventDefault();

    const dropspot: HTMLSpanElement = (e.target as HTMLSpanElement).closest(".dropspot");

    // apply a "hovered" outline if the currently-hovered-over element is a valid dropspot
    if (!!dropspot) {
      dropspot.classList.add("hovered");
    }
  };

  const handleDragLeave = (e: DragEvent<HTMLSpanElement>) => {
    const dropspot: HTMLSpanElement = (e.target as HTMLSpanElement).closest(".dropspot");

    if (!!dropspot) {
      dropspot.classList.remove("hovered");
    }
  };

  const handleDrop = (e: DragEvent<HTMLSpanElement>) => {
    handleDragLeave(e);

    if (!(e.target as HTMLSpanElement).closest(".dropspot")) {
      return;
    }

    const items: JSX.Element[] = cloneDeep(props.items);
    const draggedItemNode: JSX.Element = items.find(x => x.props.id === draggedItem.firstElementChild?.id);
    const fromIndex: number = items.indexOf(draggedItemNode);
    const dropspot: HTMLSpanElement = (e.target as HTMLSpanElement).closest(".dropspot");
    const dropspotItemNode: JSX.Element = items.find(x => x.props.id === dropspot.firstElementChild?.id);
    const dropspotItemNodeIndex: number = items.indexOf(dropspotItemNode);

    items.splice(fromIndex, 1);
    items.splice(dropspotItemNodeIndex, 0, draggedItemNode);

    handleDragEnd();
    props.onDragAndDropChange(items);

    draggedItem = null;
  };

  return (
    <span
      onDragEnter={(e) => handleDragOver(e)}
      onDragOver={(e) => handleDragOver(e)}
      onDragLeave={(e) => handleDragLeave(e)}
      onDrop={(e) => handleDrop(e)}
      ref={containerRef}
    >
      {props.items.map((item, index) => {
        return (
          <span
            data-group-id={index}
            key={index}
            className="drag-and-drop__item"
            draggable={props.draggable}
            id={`drag-and-drop__item--${index}`}
            onDragStart={(e) => props.draggable && handleDragStart(e)}
            onDragEnd={handleDragEnd}
            data-testid={`drag-and-drop__item--${index}`}
          >
            {item}
          </span>
        );
      })}
    </span>
  );
};

export default DragAndDropReordering;
